/**
 * @fileoverview Calls to `/api/v1/access/*` and the access-request endpoints,
 * typed with the Access models. Thin: no state, no rules.
 */
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { ApiService } from '../../../../core/services/api.service';
import {
  AccessChange,
  AccessRoleListItem,
  AccessUserListItem,
  RoleAccess,
  SaveUserAccessBody,
  UserAccess,
} from './access.models';

export type UserFilter = 'all' | 'custom' | 'attention';

export interface UserRecord {
  id: string;
  pid: number;
  email: string;
  firstName: string;
  lastName: string | null;
  phoneNumber: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED' | 'BLOCKED';
  isTwoFAEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AccessRequestItem {
  id: number;
  firstName: string;
  lastName: string | null;
  email: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  kind: 'SIGN_UP' | 'PRODUCT';
  requesterUserId: string | null;
  note: string | null;
  products: { pid: number; name: string; keyname: string }[];
  createdAt: string;
}

/** True when the server refused the caller's role (403), as opposed to any other failure. */
export function isForbidden(error: unknown): boolean {
  /* The request layer hands back the body, flattened (`code`) or as the API sent it (`error.code`). */
  const body = error as { code?: string; error?: { code?: string } | string; statusCode?: number } | null;
  const nested = typeof body?.error === 'object' ? body.error.code : undefined;
  return body?.code === 'FORBIDDEN' || nested === 'FORBIDDEN' || body?.statusCode === 403;
}

interface Envelope<T> {
  data: T;
  meta?: { pagination?: { total: number } };
}

@Injectable({ providedIn: 'root' })
export class AccessApiService {
  private api = inject(ApiService);

  listUsers(query: { search?: string; filter: UserFilter }): Observable<{ users: AccessUserListItem[]; total: number }> {
    const params = { limit: 100, filter: query.filter, ...(query.search ? { search: query.search } : {}) };
    return this.api
      .get<Envelope<{ users: AccessUserListItem[] }>>('/access/users', params, { rawBody: true, showLoader: false })
      .pipe(map((body) => ({ users: body.data.users, total: body.meta?.pagination?.total ?? body.data.users.length })));
  }

  getUser(userId: string): Observable<UserAccess> {
    return this.api.get<UserAccess>(`/access/users/${userId}`, undefined, { showLoader: false });
  }

  saveUser(userId: string, body: SaveUserAccessBody): Observable<UserAccess> {
    return this.api.put<UserAccess>(`/access/users/${userId}`, body, {
      showSuccessToaster: true,
      successMessage: 'Access saved',
    });
  }

  userHistory(userId: string): Observable<AccessChange[]> {
    return this.api
      .get<{ changes: AccessChange[] }>(`/access/users/${userId}/history`, { limit: 50 }, { showLoader: false })
      .pipe(map((data) => data.changes));
  }

  /** Whether the caller may use the Access screens; quiet, so a refusal shows no toaster. */
  canManage(): Observable<boolean> {
    return this.api
      .get<unknown>('/access/roles', undefined, { showLoader: false, showErrorToaster: false })
      .pipe(map(() => true));
  }

  listRoles(): Observable<AccessRoleListItem[]> {
    return this.api
      .get<{ roles: AccessRoleListItem[] }>('/access/roles', undefined, { showLoader: false })
      .pipe(map((data) => data.roles));
  }

  getRole(roleId: string): Observable<RoleAccess> {
    return this.api.get<RoleAccess>(`/access/roles/${roleId}`, undefined, { showLoader: false });
  }

  saveRolePages(roleId: string, routeIds: string[], note: string): Observable<RoleAccess> {
    const trimmed = note.trim();
    return this.api.put<RoleAccess>(
      `/access/roles/${roleId}/pages`,
      { routeIds, ...(trimmed === '' ? {} : { note: trimmed }) },
      { showSuccessToaster: true, successMessage: 'Role pages saved' },
    );
  }

  roleHistory(roleId: string): Observable<AccessChange[]> {
    return this.api
      .get<{ changes: AccessChange[] }>(`/access/roles/${roleId}/history`, { limit: 50 }, { showLoader: false })
      .pipe(map((data) => data.changes));
  }

  listRequests(query: { status?: string; kind?: string }): Observable<AccessRequestItem[]> {
    /* This endpoint's legacy map re-wraps the list as `{ success, data: [...] }` for the old screen. */
    type Listed = AccessRequestItem[] | { requests?: AccessRequestItem[]; data?: AccessRequestItem[] };
    return this.api
      .get<Listed>('/auth/access-request/list', { limit: 100, ...query }, { showLoader: false })
      .pipe(map((body) => (Array.isArray(body) ? body : (body.requests ?? body.data ?? []))));
  }

  decideRequest(id: number, status: 'APPROVED' | 'REJECTED', note?: string): Observable<unknown> {
    return this.api.patch(`/auth/access-request/${id}/status`, { status, ...(note ? { note } : {}) }, {
      showSuccessToaster: true,
      successMessage: status === 'APPROVED' ? 'Request approved' : 'Request declined',
    });
  }

  /** The product catalogue, as pid → name, for the tables' product columns. */
  productNames(): Observable<Map<number, string>> {
    return this.api
      .fetchProducts()
      .pipe(
        map((products) =>
          new Map(
            products
              .filter((product) => typeof product?.pid === 'number' && typeof product?.name === 'string')
              .map((product) => [product.pid as number, product.name as string]),
          ),
        ),
      );
  }

  /** The account record: names, phone, status — the fields the Profile tab edits. */
  getUserRecord(userId: string): Observable<UserRecord> {
    return this.api.get<{ user: UserRecord }>(`/user/${userId}`, undefined, { showLoader: false }).pipe(map((data) => data.user));
  }

  updateUser(
    userId: string,
    changes: { firstName?: string; lastName?: string; phoneNumber?: string; status?: 'ACTIVE' | 'SUSPENDED' },
    successMessage: string,
  ): Observable<unknown> {
    return this.api.put(`/user/${userId}`, changes, { showSuccessToaster: true, successMessage });
  }

  deleteUser(userId: string): Observable<unknown> {
    return this.api.delete(`/user/${userId}`, { showSuccessToaster: true, successMessage: 'User deleted' });
  }

  restoreUser(userId: string): Observable<unknown> {
    return this.api.post(`/user/${userId}/restore`, {}, { showSuccessToaster: true, successMessage: 'User restored' });
  }

  createRole(name: string, description: string): Observable<{ id: string } | null> {
    const body = { name: name.trim().toUpperCase(), ...(description.trim() ? { description: description.trim() } : {}) };
    return this.api
      .post<{ role?: { id: string } } | { id: string }>('/roles/create', body, { showSuccessToaster: true, successMessage: 'Role created' })
      .pipe(map((data) => ('role' in data && data.role ? data.role : 'id' in data ? data : null)));
  }

  updateRole(roleId: string, changes: { name?: string; description?: string; isActive?: boolean }, successMessage: string): Observable<unknown> {
    const body = { ...changes, ...(changes.name === undefined ? {} : { name: changes.name.trim().toUpperCase() }) };
    return this.api.put(`/roles/${roleId}`, body, { showSuccessToaster: true, successMessage });
  }

  requestProducts(productPids: number[], reason: string): Observable<unknown> {
    return this.api.post('/auth/access-request/products', { productPids, reason }, {
      showSuccessToaster: true,
      successMessage: 'Request sent to an administrator',
    });
  }
}
